import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useCart } from '../CartContext';
import { useSession } from '../SessionContext';
import { placeOrder, createPaymentSession, completePayment, refreshPaymentSession } from '../api';
import { WALLET_TEST } from '../walletTest';
import { v4 as uuidv4 } from 'uuid';
import MembershipModal from './MembershipModal';
import TermsModal from './TermsModal';
import DojoCardPayment from './DojoCardPayment';
import CheckoutLegal from './CheckoutLegal';
import {
  savePendingPayment, clearPendingPayment, clearPlainPendingPayment, clearPendingPaymentFor,
  readConfirmingPaymentFor, readConfirmingPaymentElsewhere, isDefinitiveEnd,
} from '../pendingPayment';
import './Checkout.css';

function formatPrice(amount) {
  return `£${Number(amount).toFixed(2)}`;
}

const receivedMessage = (orderNumber) => (orderNumber
  ? `Payment received — your order will appear shortly. Please show staff your order number ${orderNumber} if it doesn't.`
  : "Payment received — your order will appear shortly. Please show staff if it doesn't.");
// The outcome of an earlier card payment is unknown (it may or may not have gone through).
const CHECKING_MESSAGE = "We're checking your card payment. Tap Check again to see if it went through, or Continue paying by card if it didn't.";
const NEEDS_CHECKING_MESSAGE = 'This payment needs checking — please contact the venue';
const OTHER_VENUE_PAYMENT_MESSAGE = 'A card payment you started at another venue is still being checked. Please pay at the counter, or ask staff, until it has finished.';
const UNCONFIRMED_MESSAGE = 'We could not confirm your payment. Your payment may have been taken — please check again or contact staff.';
const hasOrderDetails = (body) => !!(body && (body.order_id || body.order_number));
const amountOf = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v));

const COMPLETE_RETRY_MS = 2000;
const COMPLETE_MAX_RETRIES = 5;

const Checkout = ({ venue, venueId, tableNumber, checkoutId: checkoutIdProp, onBack, onOrderPlaced }) => {
  const { items, total, clearCart } = useCart();
  const { sessionToken, setSessionToken, member, setMember, checkoutId: checkoutIdCtx } = useSession();
  const checkoutId = checkoutIdProp ?? checkoutIdCtx ?? null;

  const paymentMethods = venue?.payment_methods || [];
  const hasPayAtCounter = paymentMethods.includes('pay_at_counter') || paymentMethods.includes('cash');
  const hasCard = paymentMethods.includes('worldpay') || paymentMethods.includes('card');

  // An attempt that may already have been charged but whose order is not confirmed yet is
  // TERMINAL: card/counter are locked (no second charge) until Check again confirms it. A
  // record left by an earlier visit (confirming: true) starts the screen in that state.
  // unresolved = { reference, orderNumber, amount } of that earlier attempt.
  const [unresolved, setUnresolved] = useState(() => {
    const saved = readConfirmingPaymentFor(venueId);
    return saved
      ? { reference: saved.payment_reference, orderNumber: saved.order_number || null, amount: amountOf(saved.amount) }
      : null;
  });
  // The payment is confirmed but the response carried no order details to track.
  const [confirmedOrderNumber, setConfirmedOrderNumber] = useState(undefined);
  const locked = !!unresolved || confirmedOrderNumber !== undefined;
  // executePayment has been called in this screen and no definitive answer has come back yet.
  const [payStarted, setPayStarted] = useState(false);
  // No new payment and no counter order while a card payment may be under way: derived from
  // storage (a confirming record) as well as from this screen's state.
  const paymentChoiceLocked = locked || payStarted || !!readConfirmingPaymentFor(venueId);
  const [selectedPayment, setSelectedPayment] = useState(
    unresolved ? 'card' : hasPayAtCounter ? 'pay_at_counter' : hasCard ? 'card' : null
  );
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [paymentSessionUrl, setPaymentSessionUrl] = useState(null);
  const [paymentReference, setPaymentReference] = useState(null);
  const [orderNumber, setOrderNumber] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentError, setPaymentError] = useState(() => (unresolved ? CHECKING_MESSAGE : null));
  const [sessionRetryCount, setSessionRetryCount] = useState(0);
  const [dojoSession, setDojoSession] = useState(null); // { clientSessionSecret, paymentReference, amount }
  const mountedRef = useRef(true);
  const timerRef = useRef(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; clearTimeout(timerRef.current); };
  }, []);
  const wait = (ms) => new Promise((resolve) => { timerRef.current = setTimeout(resolve, ms); });
  const worldpayRef = useRef(null);
  const iframeContainerRef = useRef(null);

  // Calculate per-item discounts using membership discount rules
  const discountRules = member?.discount_rules || [];

  const { discountAmount, discountDetails } = (() => {
    if (discountRules.length === 0) return { discountAmount: 0, discountDetails: [] };

    let totalDiscount = 0;
    const details = [];

    for (const item of items) {
      // Find a matching rule for this item's product/variation
      const matchingRule = discountRules.find(rule => {
        if (!rule.products || rule.products.length === 0) return true; // applies to all products
        return rule.products.some(p => {
          const pidMatch = String(p.productId) === String(item.product_id);
          if (!p.variationId) return pidMatch; // rule applies to whole product
          return pidMatch && String(p.variationId) === String(item.variation_id);
        });
      });

      if (!matchingRule) continue;

      // Check remaining uses (null = unlimited)
      if (matchingRule.remaining !== null && matchingRule.remaining <= 0) continue;

      let itemDiscount = 0;
      if (matchingRule.type === 'percentage') {
        itemDiscount = item.price * item.quantity * (matchingRule.amount / 100);
      } else if (matchingRule.type === 'value') {
        itemDiscount = Math.min(matchingRule.amount * item.quantity, item.price * item.quantity);
      } else if (matchingRule.type === 'flat_price') {
        const diff = item.price - matchingRule.amount;
        itemDiscount = diff > 0 ? diff * item.quantity : 0;
      }

      if (itemDiscount > 0) {
        totalDiscount += itemDiscount;
        details.push({
          itemId: item.id,
          ruleId: matchingRule.id,
          type: matchingRule.type,
          amount: matchingRule.amount,
          saved: itemDiscount
        });
      }
    }

    return { discountAmount: totalDiscount, discountDetails: details };
  })();

  const hasDiscount = discountAmount > 0;
  const grandTotal = total - discountAmount;

  const handleMemberVerified = (memberData) => {
    setMember(memberData);
    if (memberData.session_token) {
      setSessionToken(memberData.session_token);
    }
    setShowMemberModal(false);
  };

  const handlePlaceOrder = async () => {
    if (submitting || submitted || paymentChoiceLocked) return;
    setSubmitting(true);
    setErrorMsg(null);

    const orderPayload = {
      venue_id: venueId,
      table_number: tableNumber,
      session_token: sessionToken,
      idempotency_key: uuidv4(),
      payment_method: selectedPayment,
      checkout_id: checkoutId || null,
      membership_id: member?.membership_id || null,
      discount_amount: discountAmount > 0 ? parseFloat(discountAmount.toFixed(2)) : 0,
      membership_redemptions: discountDetails.map(d => ({
        rule_id: d.ruleId,
        quantity: 1,
      })),
      items: items.map(item => ({
        product_id: item.product_id,
        variation_id: item.variation_id,
        quantity: item.quantity,
        modifiers: item.modifiers,
        notes: item.notes,
        unit_price: item.price,
      })),
    };

    try {
      const response = await placeOrder(orderPayload);
      if (response.session_token) {
        setSessionToken(response.session_token);
      }
      setSubmitted(true);
      clearPlainPendingPayment();
      clearCart();
      onOrderPlaced(response);
    } catch (err) {
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const handleSelectCard = useCallback(async (isRetry = false) => {
    if (locked) return; // never start a second attempt over an unresolved payment
    const saved = readConfirmingPaymentFor(venueId);
    if (saved) {
      // A possibly-charged attempt exists (e.g. this screen was re-entered): lock, don't pay again.
      setSelectedPayment('card');
      setUnresolved({ reference: saved.payment_reference, orderNumber: saved.order_number || null, amount: amountOf(saved.amount) });
      setPaymentError(CHECKING_MESSAGE);
      return;
    }
    if (readConfirmingPaymentElsewhere(venueId)) {
      // The one storage slot holds another venue's possibly-charged attempt: starting a card
      // payment here would overwrite it (and that venue's lock and recovery with it).
      setPaymentError(OTHER_VENUE_PAYMENT_MESSAGE);
      return;
    }
    setSelectedPayment('card');
    setPaymentStatus('loading');
    if (!isRetry) setPaymentError(null);
    // A new attempt never reuses an old Dojo session.
    setDojoSession(null);

    try {
      const sessionData = {
        venue_id: venueId,
        checkout_id: checkoutId || null,
        table_number: tableNumber,
        session_token: sessionToken,
        membership_id: member?.membership_id || null,
        discount_amount: discountAmount > 0 ? parseFloat(discountAmount.toFixed(2)) : 0,
        membership_redemptions: discountDetails.map(d => ({
          rule_id: d.ruleId,
          quantity: 1,
        })),
        items: items.map(item => ({
          product_id: item.product_id,
          variation_id: item.variation_id,
          quantity: item.quantity,
          modifiers: item.modifiers,
          notes: item.notes,
        })),
        ...(WALLET_TEST ? { wallet_test: true } : {}),
      };

      const result = await createPaymentSession(sessionData);
      if (!mountedRef.current) return;
      setPaymentReference(result.payment_reference);
      setOrderNumber(result.order_number || null);
      // Save payment context: the Worldpay redirect fallback (if WPCL doesn't intercept the
      // redirect) and the Dojo attempt record (recovery / lock).
      savePendingPayment({
        payment_reference: result.payment_reference,
        venue_id: venueId,
        return_url: window.location.href
      });
      if (result.processor === 'dojo') {
        setPaymentSessionUrl(null);
        setDojoSession({
          clientSessionSecret: result.client_session_secret,
          paymentReference: result.payment_reference,
          amount: result.amount ?? null,
        });
      } else {
        setPaymentSessionUrl(result.session_url);
      }
      setPaymentStatus('ready');
    } catch (err) {
      if (!mountedRef.current) return;
      setPaymentError(err.message || 'Failed to set up card payment. Please try again.');
      setPaymentStatus('failed');
    }
  }, [locked, venueId, checkoutId, tableNumber, sessionToken, member, discountAmount, discountDetails, items]);

  const retryPaymentSession = useCallback(async () => {
    if (worldpayRef.current) {
      try { worldpayRef.current.destroy(); } catch (e) { /* ignore */ }
      worldpayRef.current = null;
    }
    setPaymentSessionUrl(null);
    setPaymentReference(null);
    await handleSelectCard(true);
  }, [handleSelectCard]);

  const handleSwitchToCounter = useCallback(() => {
    if (worldpayRef.current) {
      try { worldpayRef.current.destroy(); } catch (e) { /* ignore */ }
      worldpayRef.current = null;
    }
    setSelectedPayment('pay_at_counter');
    clearPlainPendingPayment();
    setDojoSession(null);
    setPaymentSessionUrl(null);
    setPaymentReference(null);
    setPaymentStatus(null);
    setPaymentError(null);
    setSessionRetryCount(0);
  }, []);

  // An order is confirmed: the same success path as a Worldpay payment.
  const orderPlaced = (orderResult) => {
    clearPendingPayment();
    if (orderResult.session_token) {
      setSessionToken(orderResult.session_token);
    }
    clearCart();
    onOrderPlaced(orderResult);
  };

  // A confirmed body. With no order details (nothing to track) show a confirmation with
  // the known order number instead of order tracking.
  const orderConfirmed = (body, knownOrderNumber) => {
    if (hasOrderDetails(body)) { orderPlaced(body); return; }
    clearPendingPayment();
    if (body && body.session_token) setSessionToken(body.session_token);
    clearCart();
    setUnresolved(null);
    setPayStarted(false);
    setDojoSession(null);
    setPaymentError(null);
    setPaymentStatus(null);
    setConfirmedOrderNumber(knownOrderNumber || null);
  };

  // A definitive end (409 restart / 404 record not found): the attempt is over and cannot
  // have charged the customer, so the record goes and the payment choice is offered again.
  const endAttempt = (reference, message) => {
    clearPendingPaymentFor(reference);
    setUnresolved(null);
    setPayStarted(false);
    setDojoSession(null);
    setPaymentReference(null);
    setPaymentError(message);
    setPaymentStatus(null);
    setSelectedPayment(hasPayAtCounter ? 'pay_at_counter' : null);
  };

  // The payment may be taken but the order is not confirmed: lock the attempt, keep the
  // reference (and the stored record, for recovery) and offer Check again.
  const dojoUnresolved = (reference, knownOrderNumber, message) => {
    const amount = amountOf(dojoSession?.amount ?? unresolved?.amount);
    savePendingPayment({
      payment_reference: reference,
      venue_id: venueId,
      return_url: window.location.href,
      order_number: knownOrderNumber || null,
      amount,
      confirming: true,
    });
    setUnresolved({ reference, orderNumber: knownOrderNumber || null, amount });
    setDojoSession(null);
    setPaymentError(message);
    setPaymentStatus('failed');
  };

  // Fetch the order for a Dojo payment. 409 { pending: true } means Dojo has not
  // captured it yet: retry every 2s, up to 5 times, then the attempt becomes unresolved.
  // paid: Dojo (or the server) has just reported this payment as taken. Without it (Check
  // again on an earlier attempt) an unknown outcome keeps the neutral wording.
  const finishDojoOrder = async (reference, knownOrderNumber = orderNumber, paid = false) => {
    setPaymentStatus('processing');
    for (let attempt = 0; ; attempt += 1) {
      let orderResult;
      try {
        orderResult = await completePayment({ payment_reference: reference });
      } catch (err) {
        if (!mountedRef.current) return;
        if (err.status === 409 && err.body?.pending) {
          if (attempt >= COMPLETE_MAX_RETRIES) {
            dojoUnresolved(reference, knownOrderNumber, paid ? receivedMessage(knownOrderNumber) : CHECKING_MESSAGE);
            return;
          }
          await wait(COMPLETE_RETRY_MS);
          if (!mountedRef.current) return;
          continue;
        }
        if (isDefinitiveEnd(err)) {
          endAttempt(reference, err.message);
          return;
        }
        if (err.status === 400 && /does not match/i.test(err.message || '')) {
          dojoUnresolved(reference, knownOrderNumber, NEEDS_CHECKING_MESSAGE);
          return;
        }
        if (!paid) {
          dojoUnresolved(reference, knownOrderNumber, CHECKING_MESSAGE);
          return;
        }
        dojoUnresolved(reference, knownOrderNumber, knownOrderNumber
          ? `Payment received but order creation failed. Your payment is safe — please quote order number ${knownOrderNumber} to staff.`
          : 'Payment received but order creation failed. Your payment is safe — please contact staff.');
        return;
      }
      if (!mountedRef.current) return;
      orderConfirmed(orderResult, knownOrderNumber);
      return;
    }
  };

  // Continue on the SAME attempt: a refresh reuses the same Dojo intent, so it cannot double
  // charge. The form remounts with the new secret; Pay at Counter stays locked meanwhile.
  // The Pay label and summary come from the attempt (refresh / stored record), never the live cart.
  const handleContinueCard = async () => {
    if (!unresolved) return;
    const { reference, orderNumber: knownOrderNumber } = unresolved;
    setPaymentError(null);
    setPaymentStatus('loading');
    setOrderNumber(knownOrderNumber);
    try {
      const fresh = await refreshPaymentSession(reference);
      if (!mountedRef.current) return;
      if (fresh && fresh.client_session_secret) {
        const amount = amountOf(fresh.amount ?? unresolved.amount);
        const attemptOrderNumber = fresh.order_number || knownOrderNumber || null;
        setOrderNumber(attemptOrderNumber);
        setUnresolved({ reference, orderNumber: attemptOrderNumber, amount });
        setDojoSession({ clientSessionSecret: fresh.client_session_secret, paymentReference: reference, amount });
        setPaymentStatus('ready');
      } else {
        orderConfirmed(fresh, knownOrderNumber);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      if (isDefinitiveEnd(err)) {
        endAttempt(reference, err.message);
      } else if (err.status === 409 && err.body?.status === 'already_processed') {
        await finishDojoOrder(reference, knownOrderNumber, true);
      } else {
        dojoUnresolved(reference, knownOrderNumber, err.message || UNCONFIRMED_MESSAGE);
      }
    }
  };

  const handleCheckAgain = () => {
    if (!unresolved) return;
    setPaymentError(null);
    return finishDojoOrder(unresolved.reference, unresolved.orderNumber);
  };

  // executePayment is about to run: from now on this attempt may have been charged, so the
  // stored record is marked confirming (survives Back / reload; blocks any new session).
  const dojoPayStart = () => {
    if (!dojoSession) return;
    savePendingPayment({
      payment_reference: dojoSession.paymentReference,
      venue_id: venueId,
      return_url: window.location.href,
      order_number: orderNumber || null,
      amount: amountOf(dojoSession.amount),
      confirming: true,
    });
    setPayStarted(true);
  };

  // res.statusCode 0 = Dojo reported the payment as taken; any other result reaching here is
  // ambiguous (see DojoCardPayment), so an unknown outcome keeps the neutral wording.
  const dojoPaid = (res) => {
    setPaymentError(null);
    return finishDojoOrder(dojoSession.paymentReference, orderNumber, !!res && res.statusCode === 0);
  };

  // onError(message, retryable). Retryable (a decline / failed attempt): renew the
  // session so the form remounts; the returned promise resolves once that is done so
  // the form's Pay button stays disabled meanwhile.
  const dojoError = async (message, retryable) => {
    setPaymentError(message);
    if (!retryable) {
      if (dojoSession && (payStarted || readConfirmingPaymentFor(venueId))) {
        // The form failed after a payment was attempted: keep the attempt (Check again / Continue).
        dojoUnresolved(dojoSession.paymentReference, orderNumber, message);
        return;
      }
      setDojoSession(null);
      setPaymentStatus('failed');
      return;
    }
    const reference = dojoSession.paymentReference;
    try {
      const fresh = await refreshPaymentSession(reference);
      if (!mountedRef.current) return;
      if (fresh && fresh.client_session_secret) {
        setDojoSession((cur) => (cur ? { ...cur, clientSessionSecret: fresh.client_session_secret, amount: fresh.amount ?? cur.amount } : cur));
      } else {
        // No secret: the payment was already captured and this is the completed order.
        orderConfirmed(fresh, orderNumber);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      if (isDefinitiveEnd(err)) {
        // The attempt is over: back to the payment choice so the customer can start again.
        endAttempt(reference, err.message);
      } else if (err.status === 409 && err.body?.status === 'already_processed') {
        await finishDojoOrder(reference, orderNumber, true);
      } else {
        // 503, a network error, an amount mismatch or anything unknown: the card may have been
        // charged, so lock the attempt (record kept) instead of offering a new one.
        dojoUnresolved(reference, orderNumber, err.message || UNCONFIRMED_MESSAGE);
      }
    }
  };

  useEffect(() => {
    if (paymentStatus !== 'ready' || dojoSession || !paymentSessionUrl || !iframeContainerRef.current) return;

    if (worldpayRef.current) {
      try { worldpayRef.current.destroy(); } catch (e) { /* ignore */ }
    }

    const handleResult = async (responseData) => {
      const status = responseData?.order?.status;
      const errorReason = responseData?.order?.errorReason || responseData?.order?.description || '';

      console.log('[WORLDPAY-CLIENT] Payment status:', status, 'errorReason:', errorReason);

      switch (status) {
        case 'success':
          setPaymentStatus('processing');
          clearPendingPayment();
          try {
            const orderResult = await completePayment({
              payment_reference: paymentReference,
              worldpay_order_key: responseData.order.orderKey,
            });
            if (orderResult.session_token) {
              setSessionToken(orderResult.session_token);
            }
            clearCart();
            onOrderPlaced(orderResult);
          } catch (err) {
            setPaymentError(
              orderNumber
                ? `Payment received but order creation failed. Your payment is safe — please quote order number ${orderNumber} to staff.`
                : 'Payment received but order creation failed. Your payment is safe — please contact staff.'
            );
            setPaymentStatus('failed');
          }
          break;

        case 'failure':
        case 'error':
          setPaymentError(`Payment was declined${errorReason ? `: ${errorReason}` : ''}. Please try again or use a different card.`);
          retryPaymentSession();
          break;

        case 'cancelled_by_shopper':
          setPaymentError('Payment cancelled. You can try again or pay at the counter.');
          setPaymentStatus('failed');
          break;

        case 'session_expired':
          if (sessionRetryCount < 2) {
            setSessionRetryCount(prev => prev + 1);
            retryPaymentSession();
          } else {
            setPaymentError('Payment session expired. Please try again.');
            setPaymentStatus('failed');
          }
          break;

        default:
          setPaymentError(`Something went wrong (status: ${status || 'unknown'}). Please try again.`);
          setPaymentStatus('failed');
      }
    };

    const initWorldpay = () => {
      console.log('[WORLDPAY-CLIENT] Setting up iframe with URL:', paymentSessionUrl);
      const appOrigin = window.location.origin;
      const libraryObject = new window.WPCL.Library();
      libraryObject.setup({
        url: paymentSessionUrl,
        type: 'iframe',
        inject: 'immediate',
        target: 'worldpay-iframe-container',
        accessibility: true,
        debug: true,
        // Result URLs must match those sent in the API request — the WPCL library
        // uses them to intercept redirects and fire resultCallback instead of navigating
        successURL: `${appOrigin}/success`,
        failureURL: `${appOrigin}/failure`,
        cancelURL: `${appOrigin}/cancel`,
        pendingURL: `${appOrigin}/pending`,
        errorURL: `${appOrigin}/error`,
        expiryURL: `${appOrigin}/expiry`,
        resultCallback: function(responseData) {
          console.log('[WORLDPAY-CLIENT] resultCallback fired:', JSON.stringify(responseData));
          handleResult(responseData);
        },
        flowCallback: function(result) {
          console.log('[WORLDPAY-CLIENT] flowCallback fired:', JSON.stringify(result));
        },
      });
      worldpayRef.current = libraryObject;
      console.log('[WORLDPAY-CLIENT] Library setup complete');

      // Apple Pay requires the "payment" permission policy on the iframe.
      // The WPCL library doesn't add this, so we set it after injection.
      const container = document.getElementById('worldpay-iframe-container');
      if (container) {
        const iframe = container.querySelector('iframe');
        if (iframe) {
          iframe.setAttribute('allow', 'payment');
        } else {
          // iframe may be injected async — observe for it
          const observer = new MutationObserver(() => {
            const el = container.querySelector('iframe');
            if (el) {
              el.setAttribute('allow', 'payment');
              observer.disconnect();
            }
          });
          observer.observe(container, { childList: true, subtree: true });
        }
      }
    };

    // If the WPCL script didn't load (e.g. mobile Safari), load it dynamically
    if (window.WPCL) {
      try {
        initWorldpay();
      } catch (err) {
        console.error('[WORLDPAY-CLIENT] Setup error:', err);
        setPaymentError('Failed to load payment form. Please try again.');
        setPaymentStatus('failed');
      }
    } else {
      const script = document.createElement('script');
      script.src = 'https://payments.worldpay.com/resources/hpp/integrations/embedded/js/hpp-embedded-integration-library.js';
      script.onload = () => {
        try {
          initWorldpay();
        } catch (err) {
          console.error('[WORLDPAY-CLIENT] Setup error after dynamic load:', err);
          setPaymentError('Failed to load payment form. Please try again.');
          setPaymentStatus('failed');
        }
      };
      script.onerror = () => {
        setPaymentError('Unable to load payment provider. Please check your connection and try again.');
        setPaymentStatus('failed');
      };
      document.head.appendChild(script);
    }

    return () => {
      if (worldpayRef.current) {
        try { worldpayRef.current.destroy(); } catch (e) { /* ignore */ }
        worldpayRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStatus, paymentSessionUrl]);

  return (
    <div className="checkout-screen">
      {/* Header */}
      <div className="checkout-header">
        <button className="checkout-back-btn" onClick={onBack} aria-label="Back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h2 className="checkout-title">Checkout</h2>
      </div>

      {/* Scrollable body */}
      <div className="checkout-content">

        {/* Order summary */}
        <section className="checkout-section">
          <h3 className="checkout-section-title">Order Summary</h3>
          {unresolved ? (
            <p className="checkout-earlier-order">
              {`Your earlier card payment${unresolved.orderNumber ? ` for order ${unresolved.orderNumber}` : ''}`}
              {unresolved.amount !== null && unresolved.amount !== undefined ? ` (${formatPrice(unresolved.amount)})` : ''}
              . Paying by card here completes that order.
            </p>
          ) : (
          <ul className="checkout-items-list">
            {items.map(item => {
              const modifierLabels = item.modifiers && Object.keys(item.modifiers).length > 0
                ? Object.values(item.modifiers).flat().join(', ')
                : null;
              return (
                <li key={item.id} className="checkout-item">
                  <div className="checkout-item-left">
                    <span className="checkout-item-qty">{item.quantity}×</span>
                    <div className="checkout-item-info">
                      <span className="checkout-item-name">
                        {item.product_name}
                        {item.variation_name && (
                          <span className="checkout-item-variation"> — {item.variation_name}</span>
                        )}
                      </span>
                      {modifierLabels && (
                        <span className="checkout-item-modifiers">+ {modifierLabels}</span>
                      )}
                    </div>
                  </div>
                  <span className="checkout-item-total">{formatPrice(item.price * item.quantity)}</span>
                </li>
              );
            })}
          </ul>
          )}
        </section>

        {/* Membership */}
        <section className="checkout-section">
          {member ? (
            <div className="checkout-member-verified">
              <div className="checkout-member-verified-info">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span className="checkout-member-name">{member.member_name}</span>
              </div>
              <span className="checkout-member-badge">Member</span>
            </div>
          ) : (
            <button className="checkout-member-btn" onClick={() => setShowMemberModal(true)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              I'm a member
            </button>
          )}
        </section>

        {/* Payment method */}
        <section className="checkout-section">
          <h3 className="checkout-section-title">Payment Method</h3>
          <div className="checkout-payment-options">
            {hasPayAtCounter && (
              <button
                className={`checkout-payment-card${selectedPayment === 'pay_at_counter' ? ' checkout-payment-card--selected' : ''}`}
                onClick={() => {
                  if (paymentChoiceLocked) return;
                  if (selectedPayment === 'card') handleSwitchToCounter(); else setSelectedPayment('pay_at_counter');
                }}
                type="button"
                disabled={paymentChoiceLocked}
              >
                <span className="checkout-payment-radio" aria-hidden="true" />
                <div className="checkout-payment-card-body">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2" />
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                  </svg>
                  <span className="checkout-payment-label">Pay at Counter</span>
                </div>
              </button>
            )}
            {hasCard && (
              <button
                className={`checkout-payment-card${selectedPayment === 'card' ? ' checkout-payment-card--selected' : ''}`}
                onClick={() => handleSelectCard()}
                type="button"
                disabled={paymentChoiceLocked || paymentStatus === 'loading' || paymentStatus === 'processing'}
              >
                <span className="checkout-payment-radio" aria-hidden="true" />
                <div className="checkout-payment-card-body">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                    <line x1="1" y1="10" x2="23" y2="10" />
                  </svg>
                  <span className="checkout-payment-label">Pay by Card</span>
                </div>
              </button>
            )}
          </div>
        </section>

        {confirmedOrderNumber !== undefined && (
          <div className="checkout-payment-confirmed" role="alert">
            {confirmedOrderNumber
              ? `Payment received — your order number is ${confirmedOrderNumber}. Please show this to staff.`
              : 'Payment received — your order is confirmed. Please show staff if you need help.'}
          </div>
        )}

        {paymentError && (
          <div className="checkout-payment-error" role="alert">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            <span>{paymentError}</span>
          </div>
        )}

        {selectedPayment === 'card' && (
          <section className="checkout-section checkout-card-section">
            {paymentStatus === 'loading' && (
              <div className="checkout-card-loading">
                <span className="checkout-spinner" aria-hidden="true" />
                <span>Setting up secure payment...</span>
              </div>
            )}
            {paymentStatus === 'processing' && (
              <div className="checkout-card-loading">
                <span className="checkout-spinner" aria-hidden="true" />
                <span>Confirming payment...</span>
              </div>
            )}
            {unresolved && !dojoSession && paymentStatus !== 'processing' && paymentStatus !== 'loading' && (
              <>
                <button type="button" className="checkout-place-order-btn" onClick={handleCheckAgain}>
                  Check again
                </button>
                <button type="button" className="checkout-place-order-btn" style={{ marginTop: 12 }} onClick={handleContinueCard}>
                  Continue paying by card
                </button>
              </>
            )}
            {dojoSession && paymentStatus === 'ready' && (
              <DojoCardPayment
                clientSessionSecret={dojoSession.clientSessionSecret}
                amountLabel={unresolved
                  ? (unresolved.amount !== null && unresolved.amount !== undefined ? formatPrice(unresolved.amount) : '')
                  : formatPrice(dojoSession.amount ?? grandTotal)}
                onPayStart={dojoPayStart}
                onPaid={dojoPaid}
                onError={dojoError}
                wallet={WALLET_TEST}
              />
            )}
            <div
              id="worldpay-iframe-container"
              ref={iframeContainerRef}
              className="checkout-card-iframe"
              style={{ display: paymentStatus === 'ready' && !dojoSession ? 'block' : 'none' }}
            />
            {venue?.card_processor === 'dojo' && <CheckoutLegal legal={venue.checkout_legal} />}
          </section>
        )}

        {/* Order total (an earlier attempt shows its own amount, never the live cart) */}
        {unresolved ? (unresolved.amount !== null && unresolved.amount !== undefined && (
        <section className="checkout-section checkout-section--total">
          <div className="checkout-total-row checkout-total-row--grand">
            <span className="checkout-total-label">Total</span>
            <span className="checkout-total-value">{formatPrice(unresolved.amount)}</span>
          </div>
        </section>
        )) : (
        <section className="checkout-section checkout-section--total">
          <div className="checkout-total-row">
            <span className="checkout-total-label">Subtotal</span>
            <span className="checkout-total-value">{formatPrice(total)}</span>
          </div>
          {hasDiscount && (
            <div className="checkout-total-row checkout-total-row--discount">
              <span className="checkout-total-label">
                Member discount
              </span>
              <span className="checkout-total-value">−{formatPrice(discountAmount)}</span>
            </div>
          )}
          <div className="checkout-total-row checkout-total-row--grand">
            <span className="checkout-total-label">Total</span>
            <span className="checkout-total-value">{formatPrice(grandTotal)}</span>
          </div>
        </section>
        )}

        {/* Terms & Conditions — always shown, non-blocking */}
        <div className="checkout-terms">
          By placing your order you agree to our{' '}
          <button
            type="button"
            className="checkout-terms-link"
            onClick={() => setShowTerms(true)}
          >
            Terms &amp; Conditions
          </button>
          .
        </div>

        {errorMsg && (
          <div className="checkout-error" role="alert">{errorMsg}</div>
        )}
      </div>

      {/* Sticky footer — Place Order */}
      {selectedPayment === 'pay_at_counter' && hasPayAtCounter && !paymentChoiceLocked && (
        <div className="checkout-footer">
          <button
            className="checkout-place-order-btn"
            onClick={handlePlaceOrder}
            disabled={submitting || submitted || items.length === 0}
          >
            {submitting ? (
              <span className="checkout-spinner" aria-hidden="true" />
            ) : (
              `Place Order · ${formatPrice(grandTotal)}`
            )}
          </button>
        </div>
      )}

      {showMemberModal && (
        <MembershipModal
          venueId={venueId}
          onClose={() => setShowMemberModal(false)}
          onVerified={handleMemberVerified}
        />
      )}

      {showTerms && (
        <TermsModal
          html={venue?.terms_and_conditions}
          onClose={() => setShowTerms(false)}
        />
      )}
    </div>
  );
};

export default Checkout;
